/**
 * The access token, read at request time — not from React state.
 *
 * Supabase rotates the access token: on its own schedule, and every time the
 * session guard probes on window focus (`refreshSession()` is the only call that
 * can tell whether this device still holds the session). Each rotation used to
 * reach the app as a new `session` object and a new `token` string, and every
 * hook keyed on that string — threads, models, skills, permissions, the
 * document context — refetched at once: the app "refreshed" whenever the window
 * was focused. The old web never had the problem because its data layer read
 * the token from the auth client when it made a request, not from a prop.
 *
 * That is the split restored here. `AuthProvider` writes every rotation into
 * this store and keeps the *same* `session` object while the signed-in user is
 * the same (`retainSession`), so nothing keyed on it re-runs; every request
 * builds its header with `bearer(token)`, which sends the freshest token for
 * that sign-in rather than the one captured when the caller rendered. The
 * `token` a caller holds still means "signed in" and still works as a fallback.
 */

let current: string | null = null;

export function setCurrentAccessToken(token: string | null): void {
  current = token;
}

export function currentAccessToken(): string | null {
  return current;
}

/** The Authorization header for a caller that holds `token`: the freshest one. */
export function bearer(token: string): string {
  return `Bearer ${current ?? token}`;
}

/**
 * The provider's rule for an auth event: a rotation for the **same user** keeps
 * the previous session object (its token is stale, and `bearer` covers that);
 * a sign-in, a sign-out or a different user is a new session.
 */
export function retainSession<T extends { access_token: string; user?: { id?: string } | null }>(prev: T | null, next: T | null): T | null {
  if (!prev || !next) return next;
  const prevUser = prev.user?.id;
  return prevUser && prevUser === next.user?.id ? prev : next;
}

/** How close to expiry a token may be before a focus probe is worth a refresh. */
export const REFRESH_MARGIN_SEC = 60;

/**
 * Does the access token need refreshing now? Supabase stamps `expires_at` in
 * seconds since the epoch. The session guard probes on window focus with
 * `refreshSession()`, which mints a new token every time; the owner's rule
 * (2026-08-25) is to spend that only when the token is expired or about to be —
 * a valid token proves nothing new by being re-issued. An unknown expiry is
 * treated as due, so a session without the stamp is still verified.
 */
export function tokenNeedsRefresh(expiresAtSec: number | null | undefined, nowMs: number = Date.now(), marginSec: number = REFRESH_MARGIN_SEC): boolean {
  if (typeof expiresAtSec !== "number" || !Number.isFinite(expiresAtSec)) return true;
  return expiresAtSec - nowMs / 1000 <= marginSec;
}
